import { patchOrganizationUser } from "@/api/endpoints/organization";
import { Spinner } from "@components/ui/spinner";
import { CardContent } from "@components/ui/card";
import {
  FieldGroup,
  FieldLabel,
  FieldDescription,
  FieldError,
  Field,
} from "@components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@components/ui/input-group";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ClockIcon } from "lucide-react";
import { SubmitHandler, useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";
import { Organization } from "@/types/Organization";
import { Switch } from "@components/ui/switch";
import { Button } from "@components/ui/button";
import { useEffect } from "react";
import { useUser } from "@/hooks/useUser";
import { useParams } from "react-router-dom";
import { Skeleton } from "@components/ui/skeleton";
import ErrorComponent from "@components/ui/error";

const accountFormSchema = z.object({
  send_mail_on_weekends: z.boolean(),
  preferred_mail_delivery_time: z.string(),
});

type AccountFormValues = z.infer<typeof accountFormSchema>;

export default function EmailForm() {
  const { t } = useLingui();

  // URL state
  const { orgUuid } = useParams<{ orgUuid: Organization["uuid"] }>();

  // Server state
  const queryClient = useQueryClient();

  const {
    data: userData,
    error: userError,
    isLoading: isUserLoading,
  } = useUser();

  const user = userData?.ok ? userData.data : undefined;

  const { mutateAsync: mutateUpdateUser } = useMutation({
    mutationFn: (values: AccountFormValues) => {
      if (!user?.id) {
        throw new Error();
      }

      return patchOrganizationUser({
        user_id: user.id,
        organization_uuid: orgUuid,
        user: values,
      });
    },
    onSuccess: (response) => {
      if (!response.ok) {
        throw new Error();
      }

      toast.success(t`Uppgifterna har sparats`);

      queryClient.invalidateQueries({
        queryKey: ["user", "me"],
      });
    },
    onError: () => {
      toast.error(t`Kunde inte spara inställningarna`);
    },
  });

  // Form state
  const form = useForm<AccountFormValues>({
    resolver: zodResolver(accountFormSchema),
    defaultValues: {
      send_mail_on_weekends: false,
      preferred_mail_delivery_time: "",
    },
  });

  // Side-effects
  useEffect(() => {
    if (user) {
      form.reset({
        send_mail_on_weekends: user.send_mail_on_weekends,
        preferred_mail_delivery_time: user.preferred_mail_delivery_time,
      });
    }
  }, [user, form]);

  // Handlers
  const handleSubmit: SubmitHandler<AccountFormValues> = (values) => {
    return mutateUpdateUser(values);
  };

  return userData ? (
    userData.ok ? (
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <CardContent className="px-6">
          <FieldGroup>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Controller
                name="send_mail_on_weekends"
                control={form.control}
                render={({ field, fieldState }) => {
                  const id = "account-weekend-emails";
                  return (
                    <Field
                      orientation="horizontal"
                      data-invalid={fieldState.invalid}
                      className="items-start justify-between"
                    >
                      <div className="space-y-2">
                        <FieldLabel htmlFor={id}>
                          <Trans>Skicka e-post på helger</Trans>
                        </FieldLabel>
                        <FieldDescription>
                          <Trans>
                            Om den är avstängd pausar vi uppdateringar på lördag
                            och söndag.
                          </Trans>
                        </FieldDescription>
                      </div>
                      <Switch
                        id={id}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="send_mail_on_weekends"
                      />
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  );
                }}
              />

              <Controller
                name="preferred_mail_delivery_time"
                control={form.control}
                render={({ field, fieldState }) => {
                  const id = "account-preferred-mail-delivery-time";
                  return (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={id}>
                        <Trans>
                          Skicka dagliga och veckovisa rapporter kl:
                        </Trans>
                      </FieldLabel>
                      <InputGroup>
                        <InputGroupAddon>
                          <ClockIcon />
                        </InputGroupAddon>
                        <InputGroupInput
                          {...field}
                          type="time"
                          lang="sv-SE"
                          id={id}
                          step="1"
                          data-testid="preferred_mail_delivery_time"
                        />
                      </InputGroup>
                      <FieldDescription>
                        <Trans>Gäller mail på dag- och veckobasis</Trans>
                      </FieldDescription>
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  );
                }}
              />
            </div>
          </FieldGroup>

          <Button
            type="submit"
            className="self-end"
            disabled={form.formState.isSubmitting || !form.formState.isDirty}
            data-testid="save-profile-settings"
          >
            <Trans>Spara</Trans>
            {form.formState.isSubmitting && <Spinner />}
          </Button>
        </CardContent>
      </form>
    ) : (
      <ErrorComponent
        heading={t`Åh nej!`}
        description={t`Någonting gick snett när vi försökte hämta dina kontouppgifter.`}
      />
    )
  ) : isUserLoading ? (
    <Skeleton className="h-36" />
  ) : userError ? (
    <ErrorComponent
      heading={t`Åh nej!`}
      description={t`Någonting gick snett när vi försökte hämta dina kontouppgifter.`}
      error={userError}
    />
  ) : null;
}
